import { FieldError } from 'react-hook-form';

/**
 * Error shape for a fieldset field - fieldsets have children, so their errors
 * can have a `children` property containing errors for each child field.
 */
export type FieldsetFieldError = FieldError & {
  children?: Record<number, unknown>;
};

/**
 * Express a path to a Custom Field within a Fieldset, which may or may not be
 * nested within another Fieldset
 *
 * Examples:
 * - "children.0" (direct child of root fieldset)
 * - "children.0.children.1" (child of a nested fieldset)
 *
 * Note: Depth greater than 2 is not supported for now.
 */
export type PathToFieldsetChild<Depth extends number[] = []> =
  Depth['length'] extends 2
    ? never
    :
        | `children.${number}`
        | `children.${number}.${PathToFieldsetChild<[...Depth, 0]>}`;

/**
 * Derive a property path from a PathToFieldsetChild.
 * When fieldPath is provided, returns `${fieldPath}.${property}`.
 * When fieldPath is undefined (root field), returns the property directly.
 */
export type FieldsetChildProperty<
  TPath extends PathToFieldsetChild | undefined,
  TProperty extends string,
> = TPath extends PathToFieldsetChild ? `${TPath}.${TProperty}` : TProperty;

/**
 * Get a typed property path for a fieldset child field.
 * When fieldPath is undefined (root field), returns the property directly.
 * When fieldPath is defined, returns `${fieldPath}.${property}`.
 */
export function appendPath<
  TPath extends PathToFieldsetChild | undefined,
  TProperty extends string,
>(path: TPath, property: TProperty): FieldsetChildProperty<TPath, TProperty> {
  if (!path) return property as FieldsetChildProperty<TPath, TProperty>;
  return `${path}.${property}` as FieldsetChildProperty<TPath, TProperty>;
}
